import {
  PMaxCore,
  AuthenticationManager,
  Account,
  Actor,
  PermissionContext,
  RoleNames
} from 'core';
import { NetworkState } from 'components/NetworkState';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { AppHeaderModel, DefaultAppHeaderModel } from 'components/AppHeader';
import { DefaultAppMenuBarModel, AppMenuBarModel } from 'components/AppMenuBar';
import defaultMenus from './defaultMenus';
import { OrdersModel, DefaultOrdersModel } from 'containers/Orders/OrdersModel';
import {
  CreativesModel,
  DefaultCreativesModel
} from 'containers/Creatives/CreativesModel';
import {
  AdvertisersModel,
  DefaultAdvertisersModel
} from 'containers/Advertisers/AdvertisersModel';
import {
  SegmentsModel,
  DefaultSegmentsModel
} from 'containers/Segments/SegmentsModel';
import {
  ConversionsModel,
  DefaultConversionsModel
} from 'containers/Conversions/ConversionsModel';
import {
  DefaultAgenciesModel,
  AgenciesModel
} from 'containers/Agencies/AgenciesModel';
import {
  AccountsModel,
  DefaultAccountsModel
} from 'containers/Accounts/AccountsModel';
import { OEMAccountsModel } from 'containers/Accounts/OEMAccountsModel';
import config from 'config';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export type MainPageState = NetworkState & {
  permissionContext: PermissionContext;
};
export interface MainPageModel {
  readonly core: PMaxCore;
  readonly state: MainPageState;
  readonly event: UpdateEventListener<MainPageModel>;

  readonly account: Account | null;
  readonly barModel: AppMenuBarModel;
  readonly headerModel: AppHeaderModel;
  readonly ordersModel: OrdersModel;
  readonly creativesModel: CreativesModel;
  readonly advertisersModel: AdvertisersModel;
  readonly segmentsModel: SegmentsModel;
  readonly conversionsModel: ConversionsModel;
  readonly agenciesModel: AgenciesModel;
  readonly accountsModel: AccountsModel;
  readonly oemAccountsModel: AccountsModel;
  readonly mainPagePath: string;

  init(): Promise<void>;
  switchActor(actor: Actor): Promise<void>;
}

export interface MainPageProps {
  readonly model: MainPageModel;
  readonly authenticationManager: AuthenticationManager;
}

export class DefaultMainPageModel implements MainPageModel {
  pmaxCore: PMaxCore;
  modelEvent: FireableUpdateEventListener<MainPageModel>;

  error: Error | null;
  loading: boolean;
  menuBarModel: AppMenuBarModel;
  appHeaderModel: AppHeaderModel;
  errorCode?: number;
  ordersModel: OrdersModel;
  creativesModel: CreativesModel;
  advertisersModel: AdvertisersModel;
  segmentsModel: SegmentsModel;
  conversionsModel: ConversionsModel;
  permissionContext: PermissionContext;
  agenciesModel: AgenciesModel;
  accountsModel: AccountsModel;
  oemAccountsModel: AccountsModel;
  mainPagePathMap: any;

  constructor(
    core: PMaxCore,
    private channelGroupManager: ChannelGroupManager = new DefaultChannelGroupManager()
  ) {
    this.pmaxCore = core;
    this.error = null;
    this.loading = true;
    this.modelEvent = new FireableUpdateEventListener<MainPageModel>();
    this.menuBarModel = new DefaultAppMenuBarModel(defaultMenus(this.pmaxCore));
    this.ordersModel = new DefaultOrdersModel(this.pmaxCore);
    this.creativesModel = new DefaultCreativesModel(this.pmaxCore);
    this.advertisersModel = new DefaultAdvertisersModel(this.pmaxCore);
    this.segmentsModel = new DefaultSegmentsModel(this.pmaxCore);
    this.conversionsModel = new DefaultConversionsModel(this.pmaxCore);
    this.agenciesModel = new DefaultAgenciesModel(this.pmaxCore);
    this.accountsModel = new DefaultAccountsModel(this.pmaxCore);
    this.oemAccountsModel = new OEMAccountsModel(this.pmaxCore);
    this.appHeaderModel = new DefaultAppHeaderModel(
      this.pmaxCore.authenticationManager,
      this.pmaxCore.languageManager,
      this.menuBarModel,
      this
    );
    this.permissionContext = {
      actor: core.authenticationManager.actor,
      agencyAddon: core.addonFeatureManager.addonFeature,
      allActors: core.authenticationManager.account?.actors || []
    };
    this.menuBarModel.updatePermissionContext(
      core.authenticationManager.actor,
      core.addonFeatureManager.addonFeature
    );

    const getActorsWithoutOtherAgencies = (
      actors: Actor[],
      agencyId?: number | null
    ) => {
      return actors.filter(
        (actor) => actor.agencyId === agencyId || !actor.agencyId
      );
    };

    this.core.authenticationManager.event.add((manager) => {
      const currentActor = manager.actor;
      const addonFeature = this.core.addonFeatureManager.addonFeature;
      this.permissionContext = {
        actor: currentActor,
        agencyAddon: addonFeature,
        allActors: getActorsWithoutOtherAgencies(
          manager.account?.actors || [],
          currentActor?.agencyId
        )
      };
      this.refreshAllModel();
      this.menuBarModel.updatePermissionContext(currentActor, addonFeature);
    });

    this.core.addonFeatureManager.event.add((manager) => {
      const currentActor = this.core.authenticationManager.actor;
      const addonFeature = manager.addonFeature;
      this.permissionContext = {
        actor: currentActor,
        agencyAddon: addonFeature,
        allActors: getActorsWithoutOtherAgencies(
          this.core.authenticationManager.account?.actors || [],
          currentActor?.agencyId
        )
      };
      this.refreshAllModel();
      this.menuBarModel.updatePermissionContext(currentActor, addonFeature);
    });

    this.menuBarModel.expandChangeEvent.add(() => {
      this.updateState(false);
    });
    this.mainPagePathMap = {};
    const reportMainPageRole = [RoleNames.sysAdmin, RoleNames.adsReport];
    Object.values(RoleNames).forEach((roleName) => {
      reportMainPageRole.includes(roleName)
        ? (this.mainPagePathMap[roleName] = '/reports/performance')
        : (this.mainPagePathMap[roleName] = '/orders');
    });
    this.mainPagePathMap[RoleNames.beaconAdmin] = '/beacon-messages';
    this.mainPagePathMap[RoleNames.beaconExecutive] = '/beacon-messages';
  }

  get mainPagePath() {
    if (config.mainPagePath) {
      return config.mainPagePath;
    }
    if (this.core.authenticationManager.actor) {
      return this.mainPagePathMap[
        this.core.authenticationManager.actor.roleName.toUpperCase()
      ];
    }
    return '/orders';
  }

  get core(): PMaxCore {
    return this.pmaxCore;
  }

  get event(): UpdateEventListener<MainPageModel> {
    return this.modelEvent;
  }

  get state(): MainPageState {
    return {
      error: this.error,
      loading: this.loading,
      errorCode: this.errorCode,
      permissionContext: this.permissionContext
    };
  }

  get account(): Account | null {
    return this.core.authenticationManager.account;
  }

  get barModel(): AppMenuBarModel {
    return this.menuBarModel;
  }

  get headerModel(): AppHeaderModel {
    return this.appHeaderModel;
  }

  refreshAllModel() {
    this.ordersModel = new DefaultOrdersModel(this.pmaxCore);
    this.creativesModel = new DefaultCreativesModel(this.pmaxCore);
    this.advertisersModel = new DefaultAdvertisersModel(this.pmaxCore);
    this.segmentsModel = new DefaultSegmentsModel(this.pmaxCore);
    this.conversionsModel = new DefaultConversionsModel(this.pmaxCore);
    this.agenciesModel = new DefaultAgenciesModel(this.pmaxCore);
    this.accountsModel = new DefaultAccountsModel(this.pmaxCore);
    this.oemAccountsModel = new OEMAccountsModel(this.pmaxCore);
  }

  async init() {
    if (this.core.authenticationManager.logined === true) {
      this.updateState(true);
      // TODO 如果非 sysadmin 呼叫 api 目前會 fail
      await ChannelGroupSetting.init(this.channelGroupManager);
      try {
        await this.core.authenticationManager.fetchAccount();
        await this.core.accountManager.updateLocaleMeta(
          this.core.authenticationManager.actor
        );
        this.core.addonFeatureManager.updateAddonFeature(
          this.core.accountManager.localeMeta
            ? this.core.accountManager.localeMeta.addonFeatures
            : {}
        );

        this.updateState(false);
      } catch (error) {
        this.updateState(false, error as Error);
      }
    }
  }

  async switchActor(actor: Actor) {
    this.updateState(true);
    this.errorCode = undefined;
    try {
      this.core.authenticationManager.switchActor(actor.id);
      await this.core.accountManager.updateLocaleMeta(actor);
      this.core.addonFeatureManager.updateAddonFeature(
        this.core.accountManager.localeMeta
          ? this.core.accountManager.localeMeta.addonFeatures
          : {}
      );
      this.updateState(false);
    } catch (error) {
      this.updateState(false, error as Error);
    }
  }

  updateState(loading: boolean, error: Error | null = null) {
    this.error = error;
    this.loading = loading;
    this.modelEvent.fireEvent(this);
  }
}
